import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { ExternalLink, X, Edit2 } from 'react-feather'
import { useTranslation } from 'react-i18next'
import Modal from '../../components/Modal'

const ModalContent = styled.div`
  padding: 2rem;
  background: linear-gradient(to bottom, #000000, #231f20);
  color: #ffffff;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`

const ModalTitle = styled.h2`
  color: #fff;
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0;
`

const CloseButton = styled(X)`
  cursor: pointer;
  color: #ffffff;
  width: 24px;
  height: 24px;
  border: 1px solid #565656;
  border-radius: 50%;
  padding: 0.25rem;

  &:hover {
    color: #e9ad21;
  }
`

const Section = styled.div`
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
`

const ROISectionLabel = styled.div`
  color: #e9ad21;
  font-size: 0.7rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
`

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: #2c2c2c;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
`

const InputDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

const InputRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
`

const InputDisplayValue = styled.input`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  text-align: right;
  outline: none;
  min-width: 0;
  padding: 0;
  flex: 1;

  &::placeholder {
    color: #fff;
  }
`

const InputCurrencyLabel = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
  white-space: nowrap;
`

const InputDisplaySubtext = styled.div`
  color: #ffffff;
  font-size: 0.7rem;
  white-space: nowrap;
`

const StyledInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #40444f;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #e9ad21;
  }

  &::placeholder {
    color: #6c7284;
  }
`

const SwapButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const QuickButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const QuickButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid ${({ active }) => (active ? '#e9ad21' : '#40444f')};
  background-color: ${({ active }) => (active ? '#e9ad21' : '#565656')};
  color: ${({ active }) => (active ? '#000000' : '#e9ad21')};
  font-size: 0.7rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;

  &:hover {
    border-color: #e9ad21;
    background-color: ${({ active }) => (active ? '#e9ad21' : 'rgba(233, 173, 33, 0.1)')};
  }
`

const DurationButtons = styled.div`
  display: flex;
  background-color: #565656;
  border-radius: 50px;
  overflow: hidden;
`

const DurationButton = styled.button<{ active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 50px;
  border: none;
  background-color: ${({ active }) => (active ? '#e9ad21' : 'transparent')};
  color: ${({ active }) => (active ? '#000000' : '#e9ad21')};
  font-size: 0.9rem;
  font-weight: ${({ active }) => (active ? '700' : '500')};
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  white-space: nowrap;

  &:hover {
    opacity: ${({ active }) => (active ? '1' : '0.8')};
  }
`

const ROIResult = styled.div`
  border-radius: 8px;
  border: 1px solid #565656;
  padding: 1rem;
  margin-bottom: 0.75rem;
  position: relative;
`

const ROIHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const ROIValues = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const ROIValue = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
`

const ROIPercentage = styled.div`
  font-size: 0.7rem;
  color: #ffffff;
  line-height: 1.2;
`

const EditButton = styled.button`
  background-color: #e9ad21;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0;
  flex-shrink: 0;
  color: #ffffff;

  svg {
    width: 18px;
    height: 18px;
  }
`

const APRDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
`

const APRLabel = styled.span`
  color: #fff;
  font-size: 0.9rem;
`

const APRValue = styled.span`
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
`

const Disclaimer = styled.div`
  display: flex;
  flex-direction: column;
`

const DisclaimerItem = styled.div`
  font-size: 0.75rem;
  color: #ffffff;
  line-height: 1.4;

  &:before {
    content: '•';
    margin-right: 0.5rem;
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.4;
  }
`

const GetTokenButton = styled.div`
  color: #e9ad21;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
  width: 100%;
  text-align: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
`

interface ROICalculatorModalProps {
  isOpen: boolean
  onDismiss: () => void
  apr: number
  stakeToken: string
  stakePrice?: number | null
  rewardToken?: string
  rewardPrice?: number | null
  totalStaked?: string
  rewardPerSecond?: string
  balance?: string
}

export default function ROICalculatorModal({
  isOpen,
  onDismiss,
  apr,
  stakeToken,
  stakePrice,
  rewardToken = '',
  rewardPrice,
  totalStaked,
  rewardPerSecond,
  balance
}: ROICalculatorModalProps) {
  const { t } = useTranslation()
  const [stakeAmount, setStakeAmount] = useState('')
  const [isUSD, setIsUSD] = useState(true)
  const [duration, setDuration] = useState<'1D' | '7D' | '30D' | '1Y' | '5Y'>('1Y')
  const [customAPR, setCustomAPR] = useState<string>('')
  // const [isEditingAPR, setIsEditingAPR] = useState(false)
  const isEditingAPR = false

  // 当弹框打开时清空输入框
  useEffect(() => {
    if (isOpen) {
      setStakeAmount('')
      setCustomAPR('')
    }
  }, [isOpen])

  // 计算 USD 和 Token 之间的转换
  const tokenAmount = useMemo(() => {
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) return '--'
    if (!isUSD) return stakeAmount
    // USD 输入时，根据 stakeToken price 计算对应的 token 数量
    if (!stakePrice || stakePrice <= 0) return '--'
    const usdValue = parseFloat(stakeAmount)
    const tokenValue = usdValue / stakePrice
    return tokenValue.toFixed(6)
  }, [stakeAmount, isUSD, stakePrice])

  const usdAmount = useMemo(() => {
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) return '--'
    if (isUSD) return stakeAmount
    // Token 输入时，根据 stakeToken price 计算对应的 USD 数量
    if (!stakePrice || stakePrice <= 0) return '--'
    const tokenValue = parseFloat(stakeAmount)
    const usdValue = tokenValue * stakePrice
    return usdValue.toFixed(6)
  }, [stakeAmount, isUSD, stakePrice])

  // 计算实际使用的 APR：当 totalStaked 为 0 且用户输入了 stakeAmount 时，基于输入金额重新计算 APR
  const effectiveAPR = useMemo(() => {
    if (isEditingAPR && customAPR) {
      return parseFloat(customAPR)
    }

    // totalStaked 和 rewardPerSecond 已经是解析后的 token 数量（字符串形式），直接使用 parseFloat
    const totalStakedNum = totalStaked ? parseFloat(totalStaked) : 0
    const usdValue = parseFloat(usdAmount === '--' ? '0' : usdAmount)
    const stakeTokenAmount = parseFloat(tokenAmount === '--' ? '0' : tokenAmount)

    // 如果 totalStaked 为 0 或很小（< 0.000001），且用户输入了金额，则基于用户输入的金额计算 APR
    if (totalStakedNum < 0.000001 && rewardPerSecond && (usdValue > 0 || stakeTokenAmount > 0)) {
      const secondsPerYear = 365 * 24 * 60 * 60
      // rewardPerSecond 已经是解析后的 token 数量（字符串形式），直接使用 parseFloat
      const rewardPerSecondToken = parseFloat(rewardPerSecond)
      const annualRewardTokens = rewardPerSecondToken * secondsPerYear

      // 优先使用 stakeTokenAmount（token 数量）计算
      if (stakeTokenAmount > 0) {
        if (rewardPrice && rewardPrice > 0 && stakePrice && stakePrice > 0) {
          // 有价格时：APR = (annualRewardTokens * rewardPrice) / (用户输入的 stakeAmount * stakePrice) * 100
          const annualRewardUSD = annualRewardTokens * rewardPrice
          const userStakeUSD = stakeTokenAmount * stakePrice
          if (userStakeUSD > 0) {
            return (annualRewardUSD / userStakeUSD) * 100
          }
        } else {
          // 无价格时：APR = (annualRewardTokens / 用户输入的 stakeAmount) * 100
          return (annualRewardTokens / stakeTokenAmount) * 100
        }
      } else if (usdValue > 0 && rewardPrice && rewardPrice > 0 && stakePrice && stakePrice > 0) {
        // 如果只有 USD 值，尝试通过 USD 计算
        // 先计算对应的 token 数量：usdValue / stakePrice
        const calculatedTokenAmount = usdValue / stakePrice
        if (calculatedTokenAmount > 0) {
          const annualRewardUSD = annualRewardTokens * rewardPrice
          const userStakeUSD = calculatedTokenAmount * stakePrice
          if (userStakeUSD > 0) {
            return (annualRewardUSD / userStakeUSD) * 100
          }
        }
      }
    }

    // 否则使用池子计算的 APR
    return apr
  }, [apr, totalStaked, usdAmount, tokenAmount, rewardPerSecond, rewardPrice, stakePrice, isEditingAPR, customAPR])

  // 计算 ROI（基于 USD 金额）
  const calculatedROI = useMemo(() => {
    const usdValue = parseFloat(usdAmount === '--' ? '0' : usdAmount)
    const stakeTokenAmount = parseFloat(tokenAmount === '--' ? '0' : tokenAmount)

    if ((!usdValue || usdValue <= 0) && (!stakeTokenAmount || stakeTokenAmount <= 0)) {
      return { usd: 0, token: 0, percentage: 0 }
    }

    if (effectiveAPR <= 0) {
      return { usd: 0, token: 0, percentage: 0 }
    }

    // 根据期限计算实际收益
    const daysMap = {
      '1D': 1,
      '7D': 7,
      '30D': 30,
      '1Y': 365,
      '5Y': 1825
    }
    const days = daysMap[duration]
    const years = days / 365

    // 计算 USD 收益
    const roiUSD = usdValue > 0 ? usdValue * (effectiveAPR / 100) * years : 0
    const percentage = usdValue > 0 ? (roiUSD / usdValue) * 100 : 0

    // 计算 rewardToken 数量
    let roiToken = 0
    if (rewardPrice && rewardPrice > 0) {
      // 如果有 rewardPrice，从 USD 收益计算 token 数量
      roiToken = roiUSD / rewardPrice
    } else if (stakeTokenAmount > 0) {
      // 如果没有 rewardPrice，基于质押的 token 数量直接计算 rewardToken 数量
      // 假设 rewardToken 和 stakeToken 的价值比例与 APR 相关
      roiToken = stakeTokenAmount * (effectiveAPR / 100) * years
    }

    return {
      usd: roiUSD,
      token: roiToken,
      percentage: percentage
    }
  }, [usdAmount, tokenAmount, effectiveAPR, duration, rewardPrice])

  const numericBalance = useMemo(() => {
    const v = parseFloat(balance || '0')
    return Number.isFinite(v) ? v : 0
  }, [balance])

  const handleQuickSelect = (value: string) => {
    if (value === t('yieldFarming.myBalance')) {
      // 使用实际余额
      if (numericBalance > 0) {
        if (isUSD) {
          // USD 模式：将余额转换为 USD
          if (stakePrice && stakePrice > 0) {
            const usdValue = numericBalance * stakePrice
            setStakeAmount(usdValue.toFixed(6))
          } else {
            setStakeAmount('0')
          }
        } else {
          // Token 模式：直接使用余额
          setStakeAmount(numericBalance.toFixed(6))
        }
      } else {
        setStakeAmount('0')
      }
    } else {
      const usdValue = value.replace('$', '')
      if (isUSD) {
        setStakeAmount(usdValue)
      } else {
        // 如果是 Token 模式，需要将 USD 转换为 Token
        if (stakePrice && stakePrice > 0) {
          const tokenValue = parseFloat(usdValue) / stakePrice
          setStakeAmount(tokenValue.toFixed(6))
        } else {
          setStakeAmount('0')
        }
      }
    }
  }

  const handleSwap = () => {
    // 切换时保持等值转换
    if (isUSD) {
      // 从 USD 切换到 Token
      if (stakePrice && stakePrice > 0 && stakeAmount) {
        const usdValue = parseFloat(stakeAmount)
        const tokenValue = usdValue / stakePrice
        setStakeAmount(tokenValue.toFixed(6))
      }
    } else {
      // 从 Token 切换到 USD
      if (stakePrice && stakePrice > 0 && stakeAmount) {
        const tokenValue = parseFloat(stakeAmount)
        const usdValue = tokenValue * stakePrice
        setStakeAmount(usdValue.toFixed(2))
      }
    }
    setIsUSD(!isUSD)
  }

  const handleGetToken = () => {
    window.open('https://app.usd1swap.ai/#/swap', '_blank')
  }

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{t('yieldFarming.roiCalculator')}</ModalTitle>
          <CloseButton onClick={onDismiss} />
        </ModalHeader>

        <Section>
          <ROISectionLabel>{t('yieldFarming.staked', { token: stakeToken.toUpperCase() })}</ROISectionLabel>
          <InputContainer>
            <InputDisplay>
              <InputRow>
                <InputDisplayValue
                  type="text"
                  placeholder="0.00"
                  value={stakeAmount}
                  onChange={e => {
                    const value = e.target.value.replace(/[^0-9.]/g, '')
                    setStakeAmount(value)
                  }}
                />
                <InputCurrencyLabel>{isUSD ? 'USD1' : stakeToken}</InputCurrencyLabel>
              </InputRow>
              <InputDisplaySubtext>{!isUSD ? `${usdAmount} USD1` : `${tokenAmount} ${stakeToken}`}</InputDisplaySubtext>
            </InputDisplay>
            <SwapButton onClick={handleSwap}>
              <img src="/images/yield_swap_icon.png" alt="swap" />
            </SwapButton>
          </InputContainer>
          <QuickButtons>
            <QuickButton onClick={() => handleQuickSelect('$100')}>$100</QuickButton>
            <QuickButton onClick={() => handleQuickSelect('$1000')}>$1000</QuickButton>
            <QuickButton onClick={() => handleQuickSelect(t('yieldFarming.myBalance'))}>
              {t('yieldFarming.myBalance')}
            </QuickButton>
          </QuickButtons>
        </Section>

        <Section>
          <ROISectionLabel>{t('yieldFarming.stakedFor')}</ROISectionLabel>
          <DurationButtons>
            <DurationButton active={duration === '1D'} onClick={() => setDuration('1D')}>
              1D
            </DurationButton>
            <DurationButton active={duration === '7D'} onClick={() => setDuration('7D')}>
              7D
            </DurationButton>
            <DurationButton active={duration === '30D'} onClick={() => setDuration('30D')}>
              30D
            </DurationButton>
            <DurationButton active={duration === '1Y'} onClick={() => setDuration('1Y')}>
              1Y
            </DurationButton>
            <DurationButton active={duration === '5Y'} onClick={() => setDuration('5Y')}>
              5Y
            </DurationButton>
          </DurationButtons>
        </Section>

        <Section>
          <ROIResult>
            <ROISectionLabel>{t('yieldFarming.roiAtCurrentRates')}</ROISectionLabel>
            <ROIHeader>
              <ROIValues>
                <ROIValue>
                  {rewardPrice && rewardPrice > 0
                    ? `$${calculatedROI.usd.toFixed(6)}`
                    : `${calculatedROI.token.toFixed(6)} ${rewardToken || ''}`}
                </ROIValue>
                <ROIPercentage>{calculatedROI.percentage.toFixed(2)}%</ROIPercentage>
              </ROIValues>
              {/* <EditButton
                onClick={() => {
                  setIsEditingAPR(!isEditingAPR)
                  if (!isEditingAPR) {
                    setCustomAPR(apr.toString())
                  }
                }}
              > */}
              <EditButton>
                <Edit2 size={18} color="#000000" />
              </EditButton>
            </ROIHeader>
            {isEditingAPR && (
              <StyledInput
                type="text"
                placeholder={t('yieldFarming.enterCustomAPR')}
                value={customAPR}
                onChange={e => {
                  const value = e.target.value.replace(/[^0-9.]/g, '')
                  setCustomAPR(value)
                }}
                style={{ marginTop: '0.75rem' }}
              />
            )}
          </ROIResult>
          <APRDisplay>
            <APRLabel>APR</APRLabel>
            <APRValue>
              {isEditingAPR && customAPR ? `${customAPR}%` : effectiveAPR > 0 ? `${effectiveAPR.toFixed(2)}%` : '--'}
            </APRValue>
          </APRDisplay>
        </Section>

        <Disclaimer>
          <DisclaimerItem>{t('yieldFarming.calculatedBasedOnCurrentRates')}</DisclaimerItem>
          <DisclaimerItem>{t('yieldFarming.disclaimer')}</DisclaimerItem>
        </Disclaimer>

        <GetTokenButton onClick={handleGetToken}>
          {t('yieldFarming.getToken', { token: stakeToken.toUpperCase() })} <ExternalLink size={16} />
        </GetTokenButton>
      </ModalContent>
    </Modal>
  )
}
